/**
 * Use Time Until Midnight Countdown Hook
 * Provides a countdown timer that updates every second.
 * Triggers a callback when the countdown reaches zero.
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for countdown timer functionality
 * @param {string} nextRefresh - ISO timestamp for when the countdown should end
 * @param {Function} onExpire - Callback function to call when countdown reaches zero
 * @returns {string} Formatted countdown string (e.g., "2h 30m 15s")
 */
export function useTimeUntilMidnightCountdown(nextRefresh, onExpire) {
  const [timeUntilRefresh, setTimeUntilRefresh] = useState('');
  const hasExpiredRef = useRef(false);
  const lastNextRefreshRef = useRef(nextRefresh);

  useEffect(() => {
    if (!nextRefresh) return;
    
    // Reset expired flag when nextRefresh changes
    if (lastNextRefreshRef.current !== nextRefresh) {
      hasExpiredRef.current = false;
      lastNextRefreshRef.current = nextRefresh;
    }

    const updateCountdown = () => {
      const now = new Date();
      const refresh = new Date(nextRefresh);
      const diff = refresh - now;

      if (diff <= 0) {
        setTimeUntilRefresh('Refreshing...');
        // Trigger callback when timer expires (only once per refresh period)
        if (onExpire && !hasExpiredRef.current) {
          hasExpiredRef.current = true;
          onExpire();
        }
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Omit leading zero units; pad lower units when a higher unit is shown.
      const pad = (n) => String(n).padStart(2, '0');
      let t = `${pad(seconds)}s`;
      if (minutes > 0 || hours > 0) t = `${pad(minutes)}m ${t}`;
      if (hours > 0) t = `${hours}h ${t}`;
      setTimeUntilRefresh(t);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextRefresh, onExpire]);

  return timeUntilRefresh;
}

export default useTimeUntilMidnightCountdown;
